import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { MapPin, GraduationCap, Briefcase, Database, Code2, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

function AnimatedSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

const timelineIcons = [Database, Code2, Briefcase, Briefcase];

export default function About() {
  const { t } = useLanguage();
  const qi = t.about.quickInfo;

  return (
    <section id="about" className="relative py-24 overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />

      <div className="section-container relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <p className="text-accent-400 font-mono text-sm mb-2">{t.about.subtitle}</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-heading mb-4">
              {t.about.title} <span className="gradient-text">{t.about.titleHighlight}</span>
            </h2>
            <div className="w-24 h-1 bg-linear-to-r from-accent-500 to-cyan-500 mx-auto rounded-full" />
          </div>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - About Text */}
          <AnimatedSection delay={0.2}>
            <div className="glass rounded-2xl p-8 glow-border">
              <p className="text-body leading-relaxed text-lg mb-6">
                {t.about.p1.t1}{' '}
                <span className="text-accent-400 font-semibold">{t.about.p1.company}</span>{' '}
                {t.about.p1.t2}{' '}
                <span className="text-cyan-400 font-semibold">{t.about.p1.ai}</span>{' '}
                {t.about.p1.t3}{' '}
                <span className="text-accent-400 font-semibold">{t.about.p1.dl}</span>
                {t.about.p1.t4}
              </p>
              <p className="text-body leading-relaxed text-lg mb-6">{t.about.p2}</p>
              <p className="text-muted leading-relaxed mb-8 italic">{t.about.p3}</p>

              {/* Quick Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Briefcase, label: qi.currentRole, value: qi.currentRoleValue },
                  { icon: Database, label: qi.databases, value: qi.databasesValue },
                  { icon: GraduationCap, label: qi.education, value: qi.educationValue },
                  { icon: MapPin, label: qi.location, value: qi.locationValue },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3 p-3 rounded-xl bg-surface">
                    <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
                      <Icon size={18} className="text-accent-400" />
                    </div>
                    <div>
                      <p className="text-xs text-faint uppercase tracking-wider">{label}</p>
                      <p className="text-sm text-body font-medium">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* Right - Timeline */}
          <AnimatedSection delay={0.4}>
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-heading mb-6 flex items-center gap-2">
                <span className="w-8 h-0.5 bg-accent-500" />
                {t.about.experienceJourney}
              </h3>
              {t.about.timeline.map((item, index) => {
                const Icon = timelineIcons[index] || Briefcase;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative pl-8 border-l-2 border-surface-border pb-6 last:pb-0 group"
                  >
                    <div className="absolute left-0 top-0 -translate-x-1/2 w-4 h-4 rounded-full bg-(--c-bg) border-2 border-accent-500 group-hover:bg-accent-500 transition-colors duration-300" />
                    <div className="glass rounded-xl p-4 sm:p-5 glass-hover">
                      <div className="flex items-start gap-4">
                        {item.image ? (
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full p-0.5 bg-linear-to-br from-accent-500 to-cyan-500 shrink-0">
                            <img
                              src={item.image}
                              alt={`${item.title} profile`}
                              className="w-full h-full rounded-full object-cover bg-surface"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-accent-500/15 border border-accent-500/35 flex items-center justify-center shrink-0">
                            <Icon size={22} className="text-accent-400" />
                          </div>
                        )}

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon size={16} className="text-accent-400" />
                            <span className="text-accent-400 font-mono text-xs">{item.year}</span>
                          </div>

                          <h4 className="text-heading font-semibold mb-1.5 text-sm leading-snug">{item.title}</h4>

                          {item.companyName && (
                            item.companyUrl ? (
                              <a
                                href={item.companyUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-accent-400 hover:text-accent-300 text-xs font-medium mb-2 transition-colors"
                              >
                                {item.companyName}
                                <ExternalLink size={12} />
                              </a>
                            ) : (
                              <p className="text-accent-400 text-xs font-medium mb-2">{item.companyName}</p>
                            )
                          )}

                          <p className="text-muted text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
